/* eslint-disable no-param-reassign */
/* eslint-disable react/jsx-indent */
/* eslint-disable react-hooks/rules-of-hooks */
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Grid,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  Typography,
} from '@mui/material';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { Form as FinalForm } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { Helmet } from 'react-helmet-async';
import LoadingButton from '@mui/lab/LoadingButton';
import { useConfirm } from 'material-ui-confirm';
import {
  completeCheckoutFileUploadGql,
  createCheckoutFileUploadUrlGql,
  createPaymentIntentGql,
  getDetailsGql,
  updateCheckoutGql,
} from './gql';
import ChooseTicketCategory from './components/ChooseTicketCategory';
import EventDetails from './components/EventDetails';
import AttendeeDetails from './components/AttendeeDetails';
import InvoiceAddress from './components/InvoiceAddress';
import {
  collectCheckoutFileUploadTasks,
  formatCheckoutDataToState,
  formatStateToCheckout,
} from './helper';
import Review from './components/Review';
import LinkedStepper from './components/LinkedStepper';
import { LayoutContext } from '../../stores/LayoutStore';
import { ThemeContext } from '../../stores/ThemeStore';
import Tracking from '../../components/Tracking';
import useIsIframe from '../../hooks/useIsIframe';
import i18n from '../../locale/index';
import {
  initShopSession,
  isIosSafariBrowser,
} from '../../utils/shopSession';

// ─── Step builders ────────────────────────────────────────────────────────────

const buildDefaultCheckoutSteps = (slug, t) => ([
  { id: 'chooseTicket', label: t('steps.chooseTicket') },
  { id: 'enterAttendee', label: t('steps.enterAttendee') },
  { id: 'invoiceAddress', label: t('steps.invoiceAddress') },
  { id: 'summary', label: t('steps.summary') },
]);

const buildLinkedCheckoutSteps = (stepperConfig, t) => {
  const validSteps = Array.isArray(stepperConfig?.steps) ? [...stepperConfig.steps] : [];
  if (!validSteps.length) return null;
  return validSteps
    .sort((a, b) => (a.globalIndex || 0) - (b.globalIndex || 0))
    .map((step) => ({
      id: step.id,
      owner: step.owner,
      label: step.id === 'summary'
        ? t('steps.payment')
        : (step.title || (step.titleKey ? t(step.titleKey) : step.id)),
      globalIndex: step.globalIndex,
    }));
};

const buildLinkedCheckoutFlowSteps = (linkedSteps) => {
  const ecommerceSteps = (linkedSteps || []).filter((step) => step.owner === 'ecommerce');
  if (!ecommerceSteps.length) return null;
  return ecommerceSteps.map((step) => ({
    id: step.id,
    label: step.label,
    globalIndex: step.globalIndex,
  }));
};

// ─── CheckoutPage ─────────────────────────────────────────────────────────────

function CheckoutPage() {
  const params = useParams();
  const [activeStep, setActiveStep] = useState(0);
  const [stripe, setStripe] = useState(null);
  const [stripeLoading, setStripeLoading] = useState(false);
  const [stripeElements, setStripeElements] = useState(null);
  const [checkoutProcessingLoading, setCheckoutProcessingLoading] = useState(false);
  const [nextButtonActive, setNextButtonActive] = useState(false);
  const formRef = useRef();
  const contentRef = useRef();
  const { t } = useTranslation('checkout');
  const confirm = useConfirm();
  const [searchParams, setSearchParams] = useSearchParams();
  const layoutContext = useContext(LayoutContext);
  const themeStore = useContext(ThemeContext);
  const isIframe = useIsIframe();
  const navigate = useNavigate();
  const location = useLocation();
  const shouldBootstrapSession = isIosSafariBrowser() && isIframe;
  const [sessionReady, setSessionReady] = useState(() => !shouldBootstrapSession);

  useEffect(() => {
    if (!shouldBootstrapSession) return undefined;

    let cancelled = false;
    setSessionReady(false);

    initShopSession(params.slug)
      .catch(() => null)
      .finally(() => {
        if (!cancelled) setSessionReady(true);
      });

    return () => {
      cancelled = true;
    };
  }, [params.slug, shouldBootstrapSession]);

  const { data, refetch, loading } = useQuery(getDetailsGql, {
    skip: !sessionReady,
    variables: {
      slug: params.slug,
    },
  });

  const [updateCheckout, { loading: loadingUpdateCheckout }] = useMutation(updateCheckoutGql, {
    variables: { slug: params.slug },
  });

  const [createCheckoutFileUploadUrl] = useMutation(createCheckoutFileUploadUrlGql, {
    variables: { slug: params.slug },
  });

  const [completeCheckoutFileUpload] = useMutation(completeCheckoutFileUploadGql, {
    variables: { slug: params.slug },
  });

  const [createPaymentIntent, { loading: loadingPaymentIntent }] = useMutation(createPaymentIntentGql, {
    variables: { slug: params.slug },
  });

  const details = useMemo(() => {
    if (data?.getDetails) {
      return {
        ...data?.getDetails,
        products: data.getDetails.products.map((orgProduct) => {
          const product = { ...orgProduct };
          const avail = data.getAvailabilities.availabilities.find(({ productId }) => productId === product.id);
          product.isAvailable = avail?.isAvailable;
          product.maxQuantity = avail?.maxQuantity;
          return product;
        }),
      };
    }
    return {};
  }, [data]);

  useEffect(() => {
    if (!details?.event?.frontendSettings?.i18nOverrides) return;
    Object.entries(details?.event?.frontendSettings?.i18nOverrides).forEach(([lng, namespaces]) => {
      Object.entries(namespaces).forEach(([ns, resources]) => {
        i18n.addResourceBundle(
          lng,
          ns,
          resources,
          true,
          true,
        );
      });
    });
  }, [details?.event?.frontendSettings?.i18nOverrides]);

  const stripePublishableKey = details?.event?.stripeSettings?.publishableKey;
  const stripePromise = useMemo(
    () => (stripePublishableKey ? loadStripe(stripePublishableKey) : null),
    [stripePublishableKey],
  );
  const pageTitle = details?.event?.frontendSettings?.pageTitle;
  const faviconUrl = details?.event?.frontendSettings?.assets?.favicon;

  const initValues = useMemo(() => formatCheckoutDataToState(data), [data]);
  const oneTicketOnly = details?.event?.oneTicketOnly;

  const linkedStepperConfig = details?.event?.frontendSettings?.stepperConfig;
  const isLinkedFlow = !!details?.event?.frontendSettings?.isEcommerceLinked && !!linkedStepperConfig;
  const linkedSteps = isLinkedFlow ? buildLinkedCheckoutSteps(linkedStepperConfig, t) : null;
  const linkedFlowSteps = linkedSteps ? buildLinkedCheckoutFlowSteps(linkedSteps) : null;
  const steps = linkedFlowSteps || buildDefaultCheckoutSteps(params.slug, t);
  const summaryStepIndex = Math.max(0, steps.findIndex((step) => step.id === 'summary'));
  const checkoutBusy = loadingUpdateCheckout
    || loadingPaymentIntent
    || stripeLoading
    || checkoutProcessingLoading
    || !sessionReady;

  const saveCheckoutAndUploadFiles = async (values) => {
    const fileUploadTasks = collectCheckoutFileUploadTasks(values);
    const updateValues = formatStateToCheckout(values);
    await updateCheckout({
      variables: { slug: params.slug, input: updateValues },
    });

    if (!fileUploadTasks.length) return;

    try {
      // Sequential upload keeps backend state and failure handling predictable.
      // eslint-disable-next-line no-await-in-loop
      for (const { productId, attendeeId, attendeeIndex, fieldName, file } of fileUploadTasks) {
        const uploadInput = {
          productId,
          fieldName,
          mimeType: file.type,
          size: file.size,
          ...(attendeeId != null ? { attendeeId } : { attendeeIndex }),
        };
        // eslint-disable-next-line no-await-in-loop
        const { data: uploadUrlData } = await createCheckoutFileUploadUrl({
          variables: {
            slug: params.slug,
            input: uploadInput,
          },
        });
        const uploadPayload = uploadUrlData?.createCheckoutFileUploadUrl;
        if (!uploadPayload?.uploadUrl) {
          throw new Error('Upload URL fehlt.');
        }

        // eslint-disable-next-line no-await-in-loop
        const uploadResponse = await fetch(uploadPayload.uploadUrl, {
          method: 'PUT',
          headers: {
            'Content-Type': file.type,
          },
          body: file,
        });

        if (!uploadResponse.ok) {
          throw new Error(`Upload fehlgeschlagen (${uploadResponse.status})`);
        }

        // eslint-disable-next-line no-await-in-loop
        await completeCheckoutFileUpload({
          variables: {
            slug: params.slug,
            input: {
              productId,
              fieldName,
              ...(attendeeId != null ? { attendeeId } : { attendeeIndex }),
            },
          },
        });
      }
    } catch (error) {
      await refetch().catch(() => {});
      error.stage = 'upload';
      throw error;
    }
  };

  const getStepFromLocation = useCallback(() => {
    const stepMatch = location.hash.match(/step-(\d+)/);
    return stepMatch ? Math.min(parseInt(stepMatch[1], 10), steps.length - 1) : 0;
  }, [location.hash, steps.length]);

  useEffect(() => {
    setActiveStep(getStepFromLocation());
  }, [getStepFromLocation]);

  const scrollAfterResizeRef = useRef(false);

  const scrollToTop = () => {
    const scrollOpts = { top: 0, behavior: 'smooth' };
    window.scrollTo(scrollOpts);
    document.documentElement.scrollTo(scrollOpts);
    document.body.scrollTo(scrollOpts);
    contentRef.current?.scrollIntoView({ behavior: 'smooth' });
    if (isIframe) {
      scrollAfterResizeRef.current = true;
      window.parent.postMessage({ type: 'scrollToIframeTop' }, '*');
    }
  };

  useEffect(() => {
    if (!isIframe) return undefined;
    const el = contentRef.current;
    if (!el) return undefined;
    const sendResize = () => {
      const height = el.offsetTop + el.offsetHeight + 20;
      window.parent.postMessage(
        { type: 'iframeResize', height, scrollToTop: scrollAfterResizeRef.current },
        '*',
      );
      scrollAfterResizeRef.current = false;
    };
    const observer = new ResizeObserver(sendResize);
    observer.observe(el);
    observer.observe(document.body);
    return () => observer.disconnect();
  }, [isIframe]);

  const isFirstStepRender = useRef(true);
  useEffect(() => {
    if (isFirstStepRender.current) {
      isFirstStepRender.current = false;
      return;
    }
    scrollToTop();
    if (isIframe && contentRef.current) {
      const height = contentRef.current.offsetTop + contentRef.current.offsetHeight + 20;
      window.parent.postMessage({ type: 'iframeResize', height, scrollToTop: true }, '*');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep, isIframe]);

  const handleBack = () => {
    if (formRef.current.getState().invalid) formRef.current.reset(formatCheckoutDataToState(data));
    if (activeStep > 0) navigate(`/${params.slug}/checkout#step-${activeStep - 1}`);
  };

  useEffect(() => {
    layoutContext.setSlug(params.slug);
  }, [layoutContext, params.slug]);

  useEffect(() => {
    const typographyConfig = details?.event?.frontendSettings?.themeConfig?.typography;
    themeStore.setTypography(typographyConfig || {});
  }, [details?.event?.frontendSettings?.themeConfig?.typography, themeStore]);

  useEffect(() => {
    if (!sessionReady || loading || !data?.getCheckout) return;
    if (activeStep > 0 && data.getCheckout.lineItems?.length === 0) {
      navigate(`/${params.slug}/checkout#step-0`);
    }
  }, [activeStep, data?.getCheckout, loading, navigate, params.slug, sessionReady]);

  useEffect(() => {
    if (searchParams.get('error') !== 'failedPayment' || loading) return;
    setSearchParams((params) => {
      params.delete('error');
      return params;
    });
    navigate(`/${params.slug}/checkout#step-${steps.length - 1}`, { replace: true });
    contentRef.current?.scrollIntoView();
    confirm({
      title: t('paymentErrors.headline'),
      description: t('paymentErrors.general'),
      hideCancelButton: true,
    });
  }, [confirm, loading, navigate, params.slug, searchParams, setSearchParams, steps.length, t]);

  const onSubmit = async (values) => {
    if (activeStep === summaryStepIndex) {
      let confirmationToken;
      if (data?.getCheckout?.total > 0) {
        setStripeLoading(true);
        if (!stripe || !stripeElements) {
          setStripeLoading(false);
          await confirm({
            title: t('paymentErrors.headline'),
            description: t('paymentErrors.general'),
            hideCancelButton: true,
          });
          return;
        }
        const { error: submitError, selectedPaymentMethod } = await stripeElements.submit();
        if (submitError) {
          setStripeLoading(false);
          return;
        }

        if (selectedPaymentMethod?.startsWith('cpmt_')) {
          try {
            await createPaymentIntent({
              variables: { slug: params.slug, input: { selectedPaymentMethod } },
            });
            window.location.href = `${window.location.origin}/payment-complete/${params.slug}/stripe`;
            return;
          } catch (error) {
            const checkoutErrorCode = error?.networkError?.result?.code;
            const paymentErrorCode = checkoutErrorCode
              || error?.cause?.result?.declineCode
              || error?.cause?.result?.stripeCode;
            const modalTitle = checkoutErrorCode
              ? t(['checkoutErrors.headline', 'paymentErrors.headline'])
              : t('paymentErrors.headline');
            const modalMessage = t([`coupon.errors.${paymentErrorCode}`, `paymentErrors.${paymentErrorCode}`, 'paymentErrors.general']);
            await confirm({
              title: modalTitle,
              description: modalMessage,
              hideCancelButton: true,
            });
            return;
          } finally {
            setStripeLoading(false);
          }
        }

        const { error, confirmationToken: token } = await stripe.createConfirmationToken({
          elements: stripeElements,
          return_url: `${window.location.origin}/payment-complete/${params.slug}/stripe`,
        });
        confirmationToken = token;
        if (error) {
          setStripeLoading(false);
          await confirm({
            title: t('paymentErrors.headline'),
            description: t('paymentErrors.general'),
            hideCancelButton: true,
          });
          return;
        }
      }
      try {
        const { data } = await createPaymentIntent({
          variables: { slug: params.slug, input: { confirmationTokenId: confirmationToken?.id } },
        });

        if (data?.createPaymentIntent?.status === 'requires_action') {
          try {
            const { error: actionError } = await stripe.handleNextAction({
              clientSecret: data?.createPaymentIntent?.clientSecret,
            });

            if (actionError) {
              const paymentErrorCode = actionError?.decline_code
                || actionError?.declineCode
                || actionError?.code;
              const modalMessage = t([`paymentErrors.${paymentErrorCode}`, 'paymentErrors.general']);
              await confirm({
                title: t('paymentErrors.headline'),
                description: modalMessage,
                hideCancelButton: true,
              });
              setStripeLoading(false);
              return;
            }
          } catch (error) {
            setStripeLoading(false);
            const paymentErrorCode = error?.decline_code
              || error?.declineCode
              || error?.code
              || error?.cause?.result?.declineCode
              || error?.cause?.result?.stripeCode;
            const modalMessage = t([`paymentErrors.${paymentErrorCode}`, 'paymentErrors.general']);
            await confirm({
              title: t('paymentErrors.headline'),
              description: modalMessage,
              hideCancelButton: true,
            });
            return;
          }

          window.location.href = `${window.location.origin}/payment-complete/${params.slug}/stripe`;
          return;
        }

        window.location.href = `${window.location.origin}/payment-complete/${params.slug}/stripe`;
        return;
      } catch (error) {
        setStripeLoading(false);
        if (error?.cause?.result?.code === 'InsufficientInventory') {
          const { data } = await refetch();
          const availabilities = data?.getAvailabilities?.availabilities;
          await confirm({
            title: t('error'),
            description: (
              <>
                <Typography gutterBottom>{t('insufficientInventory')}</Typography>
                <Typography sx={{ marginTop: 1 }}>{t('insufficientInventoryAffectdCategories')}</Typography>
                <ul>
                  {error?.cause?.result?.unavailableProducts?.map((productId) => (
                    <li key={productId}>
                      {details?.products?.find(({ id }) => productId === id)?.name}
                    </li>
                  ))}
                </ul>
              </>
            ),
            hideCancelButton: true,
          });
          for (const productId of error.cause.result.unavailableProducts) {
            const avail = availabilities.find((row) => row.productId === productId);
            formRef.current.change(`product_${productId}`, Math.min(avail.maxQuantity, values[`product_${productId}`]));
          }
          navigate(`/${params.slug}/checkout#step-0`);
          await updateCheckout({
            variables: { slug: params.slug, input: formatStateToCheckout(formRef.current.getState().values) },
          });
        } else {
          const checkoutErrorCode = error?.networkError?.result?.code;
          const paymentErrorCode = checkoutErrorCode
            || error?.cause?.result?.declineCode
            || error?.cause?.result?.stripeCode;
          const stripePaymentErrorCode = error?.cause?.result?.stripeCode;
          const modalTitle = checkoutErrorCode
            ? t(['checkoutErrors.headline', 'paymentErrors.headline'])
            : t('paymentErrors.headline');
          const modalMessage = t([
            `coupon.errors.${paymentErrorCode}`,
            `paymentErrors.${paymentErrorCode}`,
            `paymentErrors.${stripePaymentErrorCode}`,
            'paymentErrors.general',
          ]);
          await confirm({
            title: modalTitle,
            description: modalMessage,
            hideCancelButton: true,
          });
        }
        setStripeLoading(false);
      }
      return;
    }

    if (activeStep < steps.length - 1) {
      setCheckoutProcessingLoading(true);
      try {
        await saveCheckoutAndUploadFiles(values);
        await refetch();

        if (activeStep < steps.length - 1) {
          navigate(`/${params.slug}/checkout#step-${activeStep + 1}`);
        }
      } catch (error) {
        const isUploadError = error?.stage === 'upload';

        const modalTitle = isUploadError
          ? t('uploadErrors.headline')
          : t('checkoutErrors.headline');
        const modalDescription = isUploadError
          ? t('uploadErrors.general')
          : t('general.errors.generalError');
        await confirm({
          title: modalTitle,
          description: modalDescription,
          hideCancelButton: true,
        });
      } finally {
        setCheckoutProcessingLoading(false);
      }
    }
  };

  const renderContent = (values) => {
    const activeStepId = steps[activeStep]?.id;
    if (!sessionReady || loading) {
      return (
        <Grid sx={{ width: '100%', textAlign: 'center', minHeight: '200px' }}>
          <CircularProgress />
        </Grid>
      );
    }

    if (activeStepId === 'chooseTicket') {
      return (
        <ChooseTicketCategory
          details={details}
          values={values}
          setNextButtonActive={setNextButtonActive}
          oneTicketOnly={oneTicketOnly}
        />
      );
    }
    if (activeStepId === 'enterAttendee') {
      return (
        <AttendeeDetails
          details={details}
          values={values}
          setNextButtonActive={setNextButtonActive}
          oneTicketOnly={oneTicketOnly}
        />
      );
    }
    if (activeStepId === 'invoiceAddress') {
      return (<InvoiceAddress details={details} values={values} />);
    }

    if (activeStepId === 'summary') {
      if ((data?.getCheckout?.total ?? 0) > 0) {
        const paymentMethodTypes = Object.entries(details.event.paymentMethods)
          .reduce((acc, [paymentMethod, value]) => {
            if (!value.enabled) return acc;
            if (paymentMethod === 'invoicePurchase') return acc;
            if (['google_pay', 'apple_pay'].includes(paymentMethod)) return acc;
            if (!value.productIds?.length) {
              acc.push(paymentMethod);
            } else if (value.productIds.some((productId) => values[`product_${productId}`])) {
              acc.push(paymentMethod);
            }
            return acc;
          }, []);

        const invoicePurchaseMethod = details.event.paymentMethods.invoicePurchase;
        let showInvoicePurchase = false;
        if (invoicePurchaseMethod?.enabled) {
          if (!invoicePurchaseMethod.productIds?.length) {
            showInvoicePurchase = true;
          } else {
            showInvoicePurchase = !!invoicePurchaseMethod.productIds.find((productId) => values[`product_${productId}`]);
          }
        }

        const customPaymentMethods = [];

        if (showInvoicePurchase) {
          customPaymentMethods.push({
            id: details.event.stripeSettings.customPaymentMethodInvoice,
            options: {
              type: 'static',
              subtitle: invoicePurchaseMethod?.subtitle
                || 'Erhalten Sie eine Rechnung per E-Mail und bezahlen Sie innerhalb von 30 Tagen per Überweisung.',
              embedded: {
                handleRender: (container) => {
                  container.innerHTML = invoicePurchaseMethod?.subtitle ? `
                      <div style="padding:12px 0;font-size:14px;line-height:1.5;color:#30313d;">
                        ${invoicePurchaseMethod?.subtitle}
                      </div>
                  ` : `
                      <div style="padding:12px 0;font-size:14px;line-height:1.5;color:#30313d;">
                        Erhalten Sie eine <strong>Rechnung per E-Mail</strong> und bezahlen Sie innerhalb von
                        <strong>30 Tagen per Überweisung</strong> – keine Kreditkarte erforderlich.
                      </div>
                    `;
                },
                handleDestroy: () => {},
              },
            },
          });
        }

        if (details.event.stripeSettings?.customPaymentMethodTest) {
          customPaymentMethods.push({
            id: details.event.stripeSettings.customPaymentMethodTest,
            options: {
              type: 'static',
              subtitle: 'Testzahlung.',
              embedded: {
                handleRender: (container) => {
                  container.innerHTML = `
                      <div style="padding:12px 0;font-size:14px;line-height:1.5;color:#30313d;">
                        Testzahlung — kein Geld wird belastet. Verwenden Sie diese Methode nur zum Testen.
                      </div>
                    `;
                },
                handleDestroy: () => {},
              },
            },
          });
        }

        const paymentMethodOrder = [
          ...paymentMethodTypes.map((key) => ({
            id: key,
            sequenceId: details.event.paymentMethods[key].sequenceId ?? Infinity,
          })),
          ...(showInvoicePurchase ? [{
            id: details.event.stripeSettings.customPaymentMethodInvoice,
            sequenceId: invoicePurchaseMethod.sequenceId ?? Infinity,
          }] : []),
          ...(details.event.stripeSettings?.customPaymentMethodTest ? [{
            id: details.event.stripeSettings.customPaymentMethodTest,
            sequenceId: Infinity,
          }] : []),
        ]
          .sort((a, b) => a.sequenceId - b.sequenceId)
          .map(({ id }) => id);

        const options = {
          mode: 'payment',
          amount: Math.round((data?.getCheckout?.total ?? 0) * 100),
          currency: 'eur',
          paymentMethodCreation: 'manual',
          payment_method_types: paymentMethodTypes,
          onBehalfOf: details.event.stripeSettings.stripeConnectAccountId,
          appearance: {
            variables: {
              colorPrimary: details?.event?.colors?.primary || '#1976d2',
            },
            theme: 'stripe',
          },
          customPaymentMethods: customPaymentMethods.length ? customPaymentMethods : undefined,
        };
        return (
          <Elements options={options} stripe={stripePromise}>
            <Review
              checkout={data?.getCheckout}
              details={details}
              values={values}
              title={isLinkedFlow ? t('steps.payment') : t('steps.review')}
              setStripe={setStripe}
              setStripeElements={setStripeElements}
              refetchParentCheckout={refetch}
              paymentMethodOrder={paymentMethodOrder}
            />
          </Elements>
        );
      }
      return (
        <Review
          checkout={data?.getCheckout}
          details={details}
          values={values}
          title={isLinkedFlow ? t('steps.payment') : t('steps.review')}
          setStripe={setStripe}
          setStripeElements={setStripeElements}
          refetchParentCheckout={refetch}
        />
      );
    }
    return null;
  };

  return (
    <FinalForm
      initialValues={initValues}
      onSubmit={onSubmit}
    >
      {({ handleSubmit, values, form }) => {
        useEffect(() => {
          formRef.current = form;
        }, [form]);

        const activeStepId = steps[activeStep]?.id;

        const content = (!sessionReady || loading) ? (
          <Grid sx={{ width: '100%', textAlign: 'center', minHeight: '200px' }}>
            <CircularProgress />
          </Grid>
        ) : (
          <>
            {isLinkedFlow ? (
              // ── Two-phase bar for linked checkout+RSVP flows ──
              <LinkedStepper
                linkedSteps={linkedSteps}
                activeStepId={activeStepId}
                theme={themeStore.theme}
              />
            ) : (
              // ── Standard MUI stepper for standalone checkout ──
              <Stepper
                activeStep={activeStep}
                sx={{ mb: 4, marginX: { xs: -2, md: 0 } }}
                alternativeLabel
              >
                {steps.map((step) => (
                  <Step key={step.id} sx={{ maxWidth: { xs: '100px', md: 'unset' }, hyphens: { xs: 'auto', md: 'unset' } }}>
                    <StepLabel>{step.label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            )}

            {renderContent(values)}
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column-reverse', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: 'stretch',
                gap: { xs: 1.5, sm: 0 },
                mt: 4,
              }}
            >
              {activeStep !== 0 ? (
                <Button
                  disabled={activeStep === 0 || checkoutBusy}
                  onClick={handleBack}
                  variant="outlined"
                  fullWidth={false}
                  sx={{ minWidth: { xs: '100%', sm: 'auto' } }}
                >
                  {t('back')}
                </Button>
              ) : (<div />)}
              {activeStep === steps.length - 1
                ? (stripeLoading) ? (
                  <LoadingButton loading variant="outlined" sx={{ minWidth: { xs: '100%', sm: 'auto' } }}>
                    Submit
                  </LoadingButton>
                ) : (
                  <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    disabled={checkoutBusy}
                    sx={{ minWidth: { xs: '100%', sm: 'auto' } }}
                  >
                    {t('submit')}
                  </Button>
                )
                : (
                <LoadingButton
                  loading={checkoutProcessingLoading}
                  variant="contained"
                  color="primary"
                  disabled={!nextButtonActive || checkoutBusy}
                  type="submit"
                  sx={{ minWidth: { xs: '100%', sm: 'auto' } }}
                >
                  {t('next')}
                </LoadingButton>
                )}
            </Box>
          </>
        );
        return (
          <form onSubmit={async (e) => {
            await handleSubmit(e);
            setTimeout(() => {
              const firstError = document.querySelector('.Mui-error');
              if (firstError) {
                firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
                if (isIframe) {
                  const offsetY = firstError.getBoundingClientRect().top + window.scrollY - 100;
                  window.parent.postMessage({ type: 'scrollToIframeOffset', offsetY }, '*');
                }
              }
            }, 100);
          }}
          >
            <Helmet>
              {pageTitle ? <title>{pageTitle}</title> : null}
              {faviconUrl ? <link rel="icon" href={faviconUrl} /> : null}
            </Helmet>
            <Tracking details={details} />
            <EventDetails details={details} />
            {isIframe ? (
              <div
                ref={contentRef}
                style={{ padding: '16px', maxWidth: '800px', margin: '0 auto' }}
              >
                {content}
              </div>
            ) : (
              <Box
                sx={{
                  ml: 10,
                  p: { xs: 2, md: 4 },
                  maxWidth: 800,
                  mx: 'auto',
                  bgcolor: 'background.paper',
                  borderRadius: { xs: 0, md: 2 },
                  boxShadow: { xs: 0, md: 3 },
                }}
                ref={contentRef}
              >
                {content}
              </Box>
            )}
          </form>
        );
      }}
    </FinalForm>
  );
}

export default CheckoutPage;
